import moment from 'moment-timezone';

import { humanize } from 'common/helpers';
import { overlayColors } from 'common/constants';

export const getSpeedValueForPoint = ({
  point,
  index,
  validPointsForLegend,
  assetType,
  showOverlay,
  multipleSpeedMarkers,
  config
}) => {
  const targetPoint = point || (validPointsForLegend[index] || null);

  if (targetPoint && targetPoint.speed !== undefined && targetPoint.speed !== null) {
    const n = Number(targetPoint.speed);
    if (typeof n === 'number' && Number.isFinite(n)) {
      return n.toFixed(2);
    }
    return showOverlay ? 'NA' : null;
  }

  const isVFD = assetType === 'VFD';
  if (isVFD) {
    return showOverlay ? 'NA' : null;
  }

  const markerSpeed = (multipleSpeedMarkers && multipleSpeedMarkers.length > 0)
    ? (() => {
      const marker = multipleSpeedMarkers.find(m => m.index === index);
      return marker && marker.speed;
    })()
    : undefined;

  const fallbackSources = [
    targetPoint && targetPoint.shaft_speed,
    targetPoint && targetPoint.spectrum && targetPoint.spectrum.shaft_speed,
    config && config.spectrum && config.spectrum.shaft_speed,
    markerSpeed
  ];

  const validSource = fallbackSources.find(source => source !== undefined && source !== null);
  if (validSource !== undefined) {
    const n = Number(validSource);
    if (typeof n === 'number' && Number.isFinite(n)) {
      return n.toFixed(2);
    }
    return showOverlay ? 'NA' : null;
  }

  return showOverlay ? 'NA' : null;
};

export const formatSpeedDisplay = ({
  speedValue,
  shaftSpeedUnit,
  config
}) => {
  if (speedValue === 'NA') {
    return 'NA';
  }

  let speedUnit = shaftSpeedUnit || 'Hz';

  if (!shaftSpeedUnit && config && config.trends_data && config.trends_data[0]) {
    speedUnit = config.trends_data[0].speed_units || 'Hz';
  }

  return `${speedValue} ${speedUnit}`;
};

export const generateTooltipText = ({
  point,
  index,
  speedValue,
  timezone,
  t,
  showOverlay,
  selectedPoints,
  chartData,
  type,
  yUnit,
  formatSpeed
}) => {
  const speedLabel = t('machineDashboard.health.charts.speedLabel', { defaultValue: 'Speed' });
  const descriptionLabel = t('machineDashboard.health.charts.descriptionLabel', { defaultValue: 'Description' });

  if (point && point.x) {
    return `${moment(point.x).tz(timezone).format('YYYY-MM-DD HH:mm:ss')} (${timezone})${
      speedValue ? ` • ${speedLabel}: ${formatSpeed(speedValue)}` : ''
    }${point.description ? `\n${descriptionLabel}: ${point.description}` : ''}`;
  }

  if (showOverlay && selectedPoints && selectedPoints.length > 0 && index < selectedPoints.length && selectedPoints[index].x) {
    return `${moment(selectedPoints[index].x).tz(timezone).format('YYYY-MM-DD HH:mm:ss')} (${timezone})${
      speedValue ? ` • ${speedLabel}: ${formatSpeed(speedValue)}` : ''
    }${selectedPoints[index].description ? `\n${descriptionLabel}: ${selectedPoints[index].description}` : ''}`;
  }

  if (chartData[index] && chartData[index][0] && chartData[index][0].y !== undefined) {
    return `${humanize(type || 'Measurement')} ${index + 1}: ${(chartData[index][0].y).toFixed(3)}${yUnit ? ` ${yUnit}` : ''}`;
  }

  return `${humanize(type || 'Measurement')} ${index + 1}`;
};

export const getLegendItemColor = ({
  point,
  index,
  showOverlay,
  chartColors
}) => {
  if (point && point.color) {
    return point.color;
  }

  if (showOverlay) {
    return overlayColors[index % overlayColors.length];
  }

  return chartColors[index % chartColors.length];
};

export const prepareLegendItemsData = ({
  isTraceOverlay,
  legendPoints,
  chartData,
  selectedPoints,
  getSpeedValue,
  getColor,
  getTooltip,
  getTraceStatusText
}) => {
  if (isTraceOverlay) {
    return legendPoints.map((pointToShow, index) => {
      const speedValue = getSpeedValue(pointToShow, index);
      const color = getColor(pointToShow, index);
      const tooltip = getTooltip(pointToShow, index, speedValue);

      return {
        index,
        pointToShow,
        speedValue,
        color,
        tooltip,
        isTraceOverlay,
        validForDisplay: true,
        traceStatusText: getTraceStatusText(pointToShow)
      };
    });
  }

  return chartData.map((_, index) => {
    const pointToShow = null;
    const speedValue = getSpeedValue(pointToShow, index);
    const color = getColor(pointToShow, index);
    const tooltip = getTooltip(
      pointToShow || (selectedPoints && selectedPoints[index]),
      index,
      speedValue
    );

    return {
      index,
      pointToShow,
      speedValue,
      color,
      tooltip,
      isTraceOverlay,
      validForDisplay: true
    };
  });
};
